import { Col, Container, Row, Stack } from 'react-bootstrap'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Button } from '../../../components/Button/Button'
import { Timer } from '../components/Timer/Timer'
import { useInPersonMeetingApp, useInPersonMeetingFileApp } from '../InPersonMeetingProvider.shared'
import { MediaDeviceSelector } from '../components/MediaDeviceSelector'
import { useBeforeUnload, useBlocker, useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { TrackEvent, useTracking } from '../../professional-app/fa/common/tracking'
import { MicVolumeLevel } from '../components/MicVolumeLevel/MicVolumeLevel'
import './RecordingView.scss'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { logout, selectAccountId } from '../../../store/auth/authSlice'
import { RoomSessionKind, useFinishSessionMutation, useLazyGetRoomSessionQuery, useStartSessionMutation } from '../../../store/media/media'
import { clearGlobalError, disableErrorMiddleware, enableErrorMiddleware, selectGlobalError } from '../../../store/error/globaleErrorSlice'
import { blobToArrayBuffer, FilePart, iDBSupported } from '../common/database'
import { FinishMeetingLoader } from '../components/FinishMeetingLoader'
import { RoomSession } from '../../../models/Room'
import { useTimeoutWorker } from '../hooks/useTimeoutWorker'
import { selectPreparationStopOnly, setPreparationStopOnly } from '../../../store/meetingSummary/display'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useSessionAvailability } from '../../../components/ScopedBlock/hooks'
import { parseError } from '../common/error'

import { parseMetadata } from '../../../common/util'
import { calcLocalStorageInfoName, generateChunkName } from '../common/util'
import { Nullable } from '../../../common/Nullable'
import { useAudioStream } from '../hooks/useAudioStream'
import { useMicMuteDetector } from '../hooks/useMicMuteDetector'
import { RecordingStatus } from '../hooks/useNativeMediaRecorder'
import { NoMicPermission } from '../components/NoMicPermission'
import { BlockerModal, ErrorModal, ExpireWarningModal, MeetingAlreadyFinishedModal, MuteWarningModal, SessionExpiredModal } from './modals/RecordingModals'
import { useLogger } from '../../../components/Logger/hooks/useLogger'
import { useNotification } from '../hooks/useNotification'
import { useTokenExpiry } from '../../../common/useTokenExpiry'

const mediaRecorderBreadcrumb = (breadcrumb?: Sentry.Breadcrumb): Sentry.Breadcrumb => ({
  type: 'info',
  level: 'info',
  category: 'MediaRecorder',
  timestamp: Date.now() / 1000,
  ...breadcrumb,
})

const fromEvent = (event: Event): Sentry.Breadcrumb => {
  const severity = event.type === 'error' ? 'error' : 'info'
  return {
    ...mediaRecorderBreadcrumb(),
    type: severity,
    level: severity,
    message: `MediaRecorder#${event.type} event fired`,
  }
}

const FOUR_HOUR_IN_MS = 4 * 60 * 60 * 1000
const TWO_MB = 2 * 1024 * 1024
const RECORDER_TIMESLICE_MS = 10000
const MUTE_PERIOD_MS = 30000

export const RecordingView: React.FC = () => {
  const logger = useLogger()
  const { getAudioChunks, deleteAudioChunks, uploadAudioChunks, uploadLogFile } = useInPersonMeetingFileApp()
  const {
    currentSession,
    setCurrentSession,
    createAudioChunk,
    mediaRecorder: { createRecorder, startRecording, stopRecording, recordingStatus, pauseRecording, resumeRecording },
    getBrowserSpec,
    currentAudioDeviceId,
    audioInputDevices,
    micPermissionError,
    mediaDevicesLoading,
  } = useInPersonMeetingApp()
  const [errorType, micErrorMessage] = micPermissionError

  const [startButtonLoading, setStartButtonLoading] = useState(false)
  const uploadPartSequence = useRef<number>(1)
  const localPartSequence = useRef<number>(1)
  const sessionId = useRef<string | null>(null)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => sessionId.current !== null && currentLocation.pathname !== nextLocation.pathname)
  const bufferSize = useRef<number>(0)
  const [showBlockerModal, setShowBlockerModal] = useState(false)
  const { trackingClient } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const [sessionFinishInProgress, setSessionFinishInProgress] = useState(false)
  const [startSession] = useStartSessionMutation()
  const [finishSession] = useFinishSessionMutation()

  const [showAlreadyFinished, setShowAlreadyFinished] = useState(false)
  const successPageHref = useRef('')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const globalError = useAppSelector(selectGlobalError)
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [triggerGetSessionById] = useLazyGetRoomSessionQuery()
  const { startTimeout, stopTimeout, isRunning: forgetTimeoutIsRunning } = useTimeoutWorker()
  const preparationStopOnly = useAppSelector(selectPreparationStopOnly)
  const { available: sessionAvailable, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()
  const { createMediaStream, getChannelCount } = useAudioStream()
  const [showMuteModal, setShowMuteModal] = useState(false)
  const originalTitleRef = useRef<string>(document.title)
  const originalFaviconRef = useRef<string | null>(null)
  const blinkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const originalFaviconPath = '/dashboard/favicon.ico'
  const warningFaviconPath = '/dashboard/warning-triangle.svg'
  const notificationListenerRef = useRef<EventListener | null>(null)
  const recorderStartEventTriggeredRef = useRef(false)
  const { status: notificationStatus } = useNotification()
  const { shouldShowExpiryWarning, timeUntilExpiry } = useTokenExpiry()
  const [showTokenExpiryWarning, setShowTokenExpiryWarning] = useState(false)

  const setFavicon = (href: string) => {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.rel = 'shortcut icon'
      document.head.appendChild(link)
    }
    link.href = href
  }

  const setWarningState = () => {
    document.title = 'No speech detected'
    setFavicon(warningFaviconPath)
  }

  const restoreOriginalState = () => {
    document.title = originalTitleRef.current
    setFavicon(originalFaviconRef.current || originalFaviconPath)
  }

  const { startListening, stopListening, pauseListening, resumeListening } = useMicMuteDetector({
    checkingTimeoutMs: 10000,
    muteHoldTimeoutMs: MUTE_PERIOD_MS,
    resendMuteDetectCallback: true,
    onMuteDetected: async () => {
      await logger.info('[RecordingView] Mute detected', {
        roomId: currentSession?.roomId,
        sessionId: currentSession?.id,
        accountId,
        timestamp: new Date().toISOString(),
      })
      if (notificationStatus === 'granted') {
        await logger.info('[RecordingView] Showing mute notification', {
          roomId: currentSession?.roomId,
          sessionId: currentSession?.id,
          accountId,
          timestamp: new Date().toISOString(),
        })
        const noti = new Notification('No Speech Detected', {
          body: 'Your meeting will end in a few minutes. Check your microphone.',
        })
        notificationListenerRef.current = () => {
          if (document.visibilityState === 'visible') {
            noti.close()
          }
        }
        document.addEventListener('visibilitychange', notificationListenerRef.current)
      }

      if (!originalFaviconRef.current) {
        const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null
        originalFaviconRef.current = link?.href || originalFaviconPath
      }

      let blinkCount = 0
      const maxBlinks = 3
      const blinkInterval = 500
      if (blinkTimerRef.current) {
        clearInterval(blinkTimerRef.current)
      }
      blinkTimerRef.current = setInterval(() => {
        if (blinkCount % 2 === 0) {
          document.title = 'No speech detected'
          setFavicon(warningFaviconPath)
        } else {
          document.title = originalTitleRef.current
          setFavicon(originalFaviconRef.current || originalFaviconPath)
        }
        blinkCount++
        if (blinkCount >= maxBlinks * 2) {
          clearInterval(blinkTimerRef.current!)
          blinkTimerRef.current = null
          setWarningState()
        }
      }, blinkInterval)
    },
    onUnmuteDetected: () => {
      if (blinkTimerRef.current) {
        clearInterval(blinkTimerRef.current)
        blinkTimerRef.current = null
      }
      restoreOriginalState()
      setShowMuteModal(false)
      if (notificationListenerRef.current) {
        document.removeEventListener('visibilitychange', notificationListenerRef.current)
      }
    },
    onMuteCallback: async () => {
      await logger.info('[RecordingView] Mute held for 30 seconds, showing modal')
      setShowMuteModal(true)
    },
  })

  const mimeTypeRef = useRef<Nullable<string>>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const atRoomSessionLimitActions = () => {
    return sessionAvailabilitySuccess && sessionAvailable
  }

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (sessionId.current && !globalError) {
          e.preventDefault()
          e.returnValue = ''
        }
      },
      [globalError],
    ),
  )
  useEffect(() => {
    if (globalError) {
      setShowErrorModal(true)
    }
  }, [globalError])

  const hasStartedRecordingRef = useRef(false)
  const hasEverStartedRecordingRef = useRef(false)
  const { source, external: { platform = null, userAgent = null } = {} } = parseMetadata(currentSession?.metadata || '{}')
  const startedFromMobileApp = source === 'voice-memo' && !platform && currentSession?.isActive && userAgent

  const _safeTrack = useCallback(
    async (eventName: string, eventDetails: Record<string, unknown>) => {
      try {
        trackingClient.track({
          eventName,
          eventDetails,
        })
      } catch (error: unknown) {
        Sentry.captureException(error)
        const { message } = parseError(error)
        await logger.error('[RecordingView] Error occurred during tracking', message)
      }
    },
    [logger, trackingClient],
  )

  const _upload = useCallback(
    async (accountId: string, session: RoomSession, parts: FilePart[]) => {
      const chunkFileName = generateChunkName(session.roomId, session.id, uploadPartSequence.current.toString())
      if (parts && parts.length) {
        try {
          await uploadAudioChunks(
            accountId,
            session.roomId,
            session.id,
            chunkFileName,
            uploadPartSequence.current,
            parts.map((p) => new Blob([p.fileContent], { type: p.mimeType })),
          )
        } catch (error: unknown) {
          if (typeof error === 'object' && error != null && 'message' in error && typeof error.message === 'string') {
            await logger.error('[RecordingView] Error occurred during uploading audio chunk.', error.message)
            throw new Error(error.message, { cause: error })
          }
          await logger.error('[RecordingView] Error occurred during uploading audio chunk. No string message property.', error)
          throw error
        }
      }
    },
    [logger, uploadAudioChunks],
  )

  const _finishSession = useCallback(
    async (accountId: string, session: RoomSession) => {
      try {
        let alreadyFinished = false
        setSessionFinishInProgress(true)
        const persistedSession = await triggerGetSessionById({ accountId, roomId: session.roomId, sessionId: session.id }).unwrap()
        if (persistedSession && persistedSession.data && persistedSession.data.isActive) {
          const parts = await getAudioChunks(accountId, session.roomId, session.id)
          await _upload(accountId, session, parts)
          await finishSession({ accountId, sessionId: session.id, body: { roomId: session.roomId } })
          await logger.info('[RecordingView] Session finishing...', {
            accountId,
            sessionId: session.id,
            roomId: session.roomId,
          })
        } else {
          alreadyFinished = true
        }
        await deleteAudioChunks(accountId, session.roomId, session.id)
        successPageHref.current = _getSuccessPageHref(session)
        window.localStorage.removeItem(calcLocalStorageInfoName(accountId, session.roomId, session.id))

        if (!alreadyFinished) {
          await logger.info('[RecordingView] Session finished successfully.')
        } else {
          await logger.warn('[RecordingView] Session already finished')
          setShowAlreadyFinished(true)
        }
      } catch (error: unknown) {
        Sentry.captureException(error)
        setErrorMessage('Error occurred during finishing the meeting.')
        setShowErrorModal(true)
        const { message, status } = parseError(error)
        await logger.error('[RecordingView] Error occurred during finishing the meeting.', error)
        await _safeTrack(TrackEvent.IN_PERSON_MEETING_FINISHING_ERROR, {
          platform: 'desktop',
          roomId: session.roomId,
          sessionId: session.id,
          errorMessage: message,
          errorCode: status,
        })
      } finally {
        uploadPartSequence.current = 0
        localPartSequence.current = 0
        bufferSize.current = 0
        setCurrentSession(null)
        sessionId.current = null
        dispatch(enableErrorMiddleware())
        const logTraceId = `in-person-meeting-${accountId}-${session.roomId}-${session.id}`
        const logs = await logger.getLogsByTraceId(logTraceId)
        if (logs) {
          await uploadLogFile(accountId, session.roomId, session.id, 'voice-memo.log', logs)
          await logger.info('[RecordingView] Log file uploaded.')
        }
        await logger.deleteByTraceId(logTraceId)
      }
    },
    [triggerGetSessionById, getAudioChunks, _upload, finishSession, logger, deleteAudioChunks, setCurrentSession, dispatch, uploadLogFile, _safeTrack],
  )

  const _stopRecording = useCallback(async () => {
    if (recordingStatus !== RecordingStatus.STOPPED) {
      stopRecording()
      await _safeTrack(TrackEvent.IN_PERSON_MEETING_FINISHED, {
        platform: 'desktop',
        roomId: currentSession?.roomId,
        sessionId: currentSession?.id,
        accountId,
      })
      await logger.info('[RecordingView] MediaRecorder stopped.', {
        roomId: currentSession?.roomId,
        sessionId: currentSession?.id,
        accountId,
        timestamp: new Date().toISOString(),
      })
    } else {
      await logger.warn('[RecordingView] Recording already stopped', {
        roomId: currentSession?.roomId,
        sessionId: currentSession?.id,
        accountId,
        timestamp: new Date().toISOString(),
      })
      if (currentSession?.roomId && currentSession?.id) {
        await _finishSession(accountId, currentSession)
      }
    }
  }, [recordingStatus, stopRecording, _safeTrack, currentSession, logger, accountId, _finishSession])

  const forgetTimeoutCallback = useCallback(async () => {
    await logger.info('[RecordingView] Recording timeout, finishing the meeting automatically.', {
      accountId,
      roomId: currentSession?.roomId,
      sessionId: currentSession?.id,
    })
    stopTimeout()
    await _stopRecording()
  }, [currentSession?.roomId, currentSession?.id, accountId, logger, _stopRecording, stopTimeout])

  useEffect(() => {
    if (recordingStatus === RecordingStatus.RECORDING && !hasStartedRecordingRef.current) {
      hasStartedRecordingRef.current = true
      hasEverStartedRecordingRef.current = true
      if (!forgetTimeoutIsRunning) {
        startTimeout(async () => {
          await forgetTimeoutCallback()
        }, FOUR_HOUR_IN_MS)
        logger.info('[RecordingView] Forgetting timeout started.').then(() => {})
      }
    } else if (recordingStatus !== RecordingStatus.RECORDING) {
      hasStartedRecordingRef.current = false
    }
    return () => {
      if (recordingStatus === RecordingStatus.STOPPED) {
        stopTimeout()
      }
    }
  }, [forgetTimeoutCallback, recordingStatus, startTimeout, stopTimeout, forgetTimeoutIsRunning, logger])

  const onRecordingAvailable = useCallback(
    async (accountId: string, session: RoomSession, event: BlobEvent) => {
      try {
        const dataBlob = new Blob([event.data], { type: mimeTypeRef.current ?? 'audio/ogg' })
        bufferSize.current = bufferSize.current + dataBlob.size
        const now = new Date().toISOString()
        const blob = await blobToArrayBuffer(dataBlob)
        await createAudioChunk({
          accountId,
          roomId: session.roomId,
          sessionId: session.id,
          name: generateChunkName(session.roomId, session.id, localPartSequence.current.toString()),
          createdAt: now,
          fileContent: blob,
          mimeType: mimeTypeRef.current ?? 'audio/ogg',
          size: dataBlob.size,
          sort: localPartSequence.current,
        } as FilePart)
        localPartSequence.current++
        await logger.info('[RecordingView] Audio chunk created', {
          chunkSize: dataBlob.size,
          bufferSize: bufferSize.current,
          mimeType: mimeTypeRef.current,
        })
        if (session && bufferSize.current >= TWO_MB) {
          await logger.info('[RecordingView] Uploading audio chunk', {
            bufferSize: bufferSize.current,
            localPartSequence: localPartSequence.current,
            uploadPartSequence: uploadPartSequence.current,
          })
          const parts = await getAudioChunks(accountId, session.roomId, session.id)
          await _upload(accountId, session, parts)
          trackRecordingInLocalstorage(accountId, session.roomId, session.id)
          await deleteAudioChunks(accountId, session.roomId, session.id)
          uploadPartSequence.current++
          bufferSize.current = 0
        }
      } catch (error: unknown) {
        Sentry.captureException(error)
        await logger.error('[RecordingView] Error occurred during processing audio chunk.', error)
        const { message, status } = parseError(error)
        await _safeTrack(TrackEvent.IN_PERSON_MEETING_CHUNK_UPLOAD_FAILED, {
          platform: 'desktop',
          roomId: session.roomId,
          sessionId: session.id,
          inputDevice: audioInputDevices?.find((d: MediaDeviceInfo) => d.deviceId === currentAudioDeviceId)?.label,
          errorMessage: message,
          errorCode: status,
        })
        uploadPartSequence.current++
      }
    },
    [createAudioChunk, logger, getAudioChunks, _upload, deleteAudioChunks, _safeTrack, audioInputDevices, currentAudioDeviceId],
  )

  const isRecording = recordingStatus !== RecordingStatus.STOPPED

  useEffect(() => {
    const meetingInfo = currentSession ? window.localStorage.getItem(calcLocalStorageInfoName(accountId, currentSession?.roomId, currentSession?.id)) : null
    const sessionRecordingElsewhere = !isRecording && !!meetingInfo && !hasEverStartedRecordingRef.current
    dispatch(setPreparationStopOnly(sessionRecordingElsewhere))
  }, [accountId, currentSession, dispatch, isRecording])

  const _getSuccessPageHref = (session: RoomSession) => {
    return `/workspace/past-meetings/room/${session.roomId}/session/${session.id}`
  }

  const _startRecording = useCallback(
    async (startAnyWay: boolean = false) => {
      setStartButtonLoading(true)
      let session: Nullable<RoomSession> = null
      try {
        await logger.info('[RecordingView] Starting session...', { shouldShowExpiryWarning, timeUntilExpiry })
        if (shouldShowExpiryWarning && !startAnyWay) {
          await logger.info('[RecordingView] Refresh token about to expire, showing warning modal')
          setShowTokenExpiryWarning(true)
          return
        }
        session = await startSession({
          accountId,
          body: {
            kind: RoomSessionKind.IN_PERSON,
            startedAt: new Date().toISOString(),
            ...(currentSession ? { roomId: currentSession.roomId, sessionId: currentSession.id } : {}),
            metadata: {
              source: 'voice-memo',
              userAgent: navigator.userAgent,
              platform: 'desktop',
              audioTransferStrategy: 'chunked',
            },
          },
        }).unwrap()
      } catch (error: unknown) {
        Sentry.captureException(error)
        await _safeTrack(TrackEvent.IN_PERSON_MEETING_START_ERROR, {
          roomId: currentSession?.roomId,
          sessionId: currentSession?.id,
        })
      }

      if (!session) {
        await logger.error('[RecordingView] Failed to start session')
        setErrorMessage('Failed to start meeting session.')
        setShowErrorModal(true)
        setStartButtonLoading(false)
        return
      }

      const browserSpec = await getBrowserSpec()

      await logger.info('[RecordingView] Session started', {
        browserSpec,
        selectedAudioDevice: `${currentAudioDeviceId}--${audioInputDevices?.find((d) => d.deviceId === currentAudioDeviceId)?.label}`,
        audioInputDevices: audioInputDevices?.map((d) => `${d.deviceId}--${d.label}`),
      })
      setCurrentSession(session)
      sessionId.current = session.id
      const capturingStream = await createMediaStream({
        channelCount: 1,
        deviceId: currentAudioDeviceId || undefined,
        noiseSuppression: true,
        echoCancellation: true,
      })

      if (!capturingStream) {
        await logger.error('[RecordingView] Failed to acquire media stream')
        setErrorMessage('Failed to acquire voice capturing.')
        setShowErrorModal(true)
        return
      }

      const channelCount = capturingStream ? await getChannelCount(capturingStream) : 1
      const { id: trackId, enabled: trackEnabled, muted: trackMuted, readyState: trackReadyState } = capturingStream.getAudioTracks()[0]
      const streamInfo = {
        channelCount,
        streamId: capturingStream.id,
        active: capturingStream.active,
        track: {
          id: trackId,
          enabled: trackEnabled,
          muted: trackMuted,
          readyState: trackReadyState,
        },
        streamDeviceId: capturingStream.getAudioTracks()[0].getSettings().deviceId,
      }
      const recorder = await createRecorder(capturingStream)
      if (!recorder) {
        await logger.error('[RecordingView] Failed to start MediaRecorder')
        setShowErrorModal(true)
        setErrorMessage('Failed to start voice capturing.')
        return
      }
      await startRecording(RECORDER_TIMESLICE_MS)

      const manualStartTriggerTimeout = setTimeout(async () => {
        if (!recorderStartEventTriggeredRef.current) {
          await logger.warn('[RecordingView] MediaRecorder start event not triggered')
          await handleStart()
        }
      }, 1000)

      const handleStart = async () => {
        if (!session) {
          return
        }
        await startListening(capturingStream)
        setStartButtonLoading(false)
        trackRecordingInLocalstorage(accountId, session.roomId, session.id)
        dispatch(disableErrorMiddleware())
        await logger.info('[RecordingView] Waiting for mic to be ready')
        recorderStartEventTriggeredRef.current = true
      }

      recorder.addEventListener('dataavailable', async (event) => {
        Sentry.addBreadcrumb(fromEvent(event))
        mimeTypeRef.current = recorder.mimeType
        await logger.info('[RecordingView] MediaRecorder dataavailable event')
        if (session) {
          await onRecordingAvailable(accountId, session, event)
        }
      })

      recorder.addEventListener('start', async (event) => {
        await logger.info('[RecordingView] MediaRecorder start event', { mimeType: recorder.mimeType })
        if (recorderStartEventTriggeredRef.current) {
          await logger.info('[RecordingView] MediaRecorder start event triggered already')
          return
        }
        Sentry.addBreadcrumb(fromEvent(event))
        await handleStart()
      })

      recorder.addEventListener('pause', async (event) => {
        Sentry.addBreadcrumb(fromEvent(event))
        await logger.info('[RecordingView] MediaRecorder pause event')
        await pauseListening()
      })

      recorder.addEventListener('resume', async (event) => {
        Sentry.addBreadcrumb(fromEvent(event))
        await logger.info('[RecordingView] MediaRecorder resume event')
        await resumeListening()
      })

      recorder.addEventListener('stop', async (event) => {
        Sentry.addBreadcrumb(fromEvent(event))
        clearTimeout(manualStartTriggerTimeout)
        await logger.info('[RecordingView] MediaRecorder stop event')
        await stopListening()
        if (session) {
          await _finishSession(accountId, session)
        }
      })

      recorder.addEventListener('error', async (event) => {
        const errorEvent = event as ErrorEvent // TS 4.9.5 cannot correctly infer ErrorEvent, TS 5.x does
        Sentry.addBreadcrumb(fromEvent(errorEvent))
        Sentry.captureException(errorEvent.error)
        await logger.error('Recorder error:', errorEvent.error instanceof Error ? `[${errorEvent.error.name}]: ${errorEvent.error.message}` : 'Unknown error')
      })

      Sentry.addBreadcrumb(
        mediaRecorderBreadcrumb({
          message: '[RecordingView] MediaRecorder initialized',
          data: streamInfo,
        }),
      )
      await logger.info('[RecordingView] MediaRecorder initialized', streamInfo)
      setStartButtonLoading(false)
      await _safeTrack(TrackEvent.IN_PERSON_MEETING_STARTED, {
        eventName: TrackEvent.IN_PERSON_MEETING_STARTED,
        eventDetails: {
          platform: 'desktop',
          dataBase: iDBSupported ? 'native' : 'in-memory',
          roomId: session.roomId,
          sessionId: session.id,
          inputDevice: audioInputDevices?.find((d) => d.deviceId === currentAudioDeviceId)?.label,
        },
      })
    },
    [
      accountId,
      audioInputDevices,
      createMediaStream,
      currentAudioDeviceId,
      currentSession,
      dispatch,
      getBrowserSpec,
      getChannelCount,
      logger,
      setCurrentSession,
      startSession,
      startRecording,
      onRecordingAvailable,
      _finishSession,
      _safeTrack,
      startListening,
      stopListening,
      createRecorder,
      shouldShowExpiryWarning,
      timeUntilExpiry,
      pauseListening,
      resumeListening,
    ],
  )

  const toggleRecording = useCallback(async () => {
    if (recordingStatus === RecordingStatus.STOPPED && currentAudioDeviceId) {
      await _startRecording()
    } else if ((recordingStatus === RecordingStatus.RECORDING || recordingStatus === RecordingStatus.PAUSED) && currentSession) {
      await _stopRecording()
    }
  }, [_startRecording, _stopRecording, recordingStatus, currentAudioDeviceId, currentSession])

  const trackRecordingInLocalstorage = (accountId: string, roomId: string, sessionId: string) => {
    window.localStorage.setItem(
      calcLocalStorageInfoName(accountId, roomId, sessionId),
      JSON.stringify({
        currentSequence: uploadPartSequence.current,
        date: new Date().toISOString(),
      }),
    )
  }

  const _pauseRecording = async () => {
    pauseRecording()
    await logger.info('Recording paused')
    await _safeTrack(TrackEvent.IN_PERSON_MEETING_PAUSED, {
      platform: 'desktop',
      roomId: currentSession?.roomId,
      sessionId: currentSession?.id,
    })
  }

  const _resumeRecording = async () => {
    resumeRecording()
    await logger.info('[RecordingView] Recording resumed')
    await _safeTrack(TrackEvent.IN_PERSON_MEETING_RESUMED, {
      platform: 'desktop',
      roomId: currentSession?.roomId,
      sessionId: currentSession?.id,
    })
  }
  const togglePauseRecording = async () => {
    if (recordingStatus === RecordingStatus.RECORDING) {
      await _pauseRecording()
    } else if (recordingStatus === RecordingStatus.PAUSED) {
      await _resumeRecording()
    }
  }

  const blockerProceedAction = async () => {
    blocker.reset?.()
    await _finishSession(accountId, currentSession!)
  }

  const blockerResetAction = () => {
    setShowBlockerModal(false)
    blocker.reset?.()
  }

  const handleMuteModalTimeout = useCallback(async () => {
    await _stopRecording()
    setShowMuteModal(false)
  }, [_stopRecording])

  const errorModalBody = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Error</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>{errorMessage ?? 'An error occurred during meeting.'}</div>
        </Col>
      </Row>
    </Container>
  )

  const handleClickErrorModal = async () => {
    setShowErrorModal(false)
    await _stopRecording()
  }

  const handleSessionExpiredError = async () => {
    dispatch(clearGlobalError())
    setShowSessionExpiredModal(false)
    dispatch(logout({ location: `/workspace/live-meetings/room/${currentSession?.roomId}/session/${currentSession?.id}` }))
  }

  const isSameLocalDate = (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate()
  }

  if (errorType && micErrorMessage) {
    return <NoMicPermission errorType={errorType} errorMessage={micErrorMessage} />
  }

  return (
    <>
      <ErrorModal show={showErrorModal} onHide={handleClickErrorModal} body={errorModalBody} onStopMeeting={handleSessionExpiredError} />
      <SessionExpiredModal show={showSessionExpiredModal} onHide={handleSessionExpiredError} onLogin={handleSessionExpiredError} />

      <BlockerModal show={blocker.state === 'blocked' || showBlockerModal} onPrimary={blockerProceedAction} onSecondary={blockerResetAction} />

      <MeetingAlreadyFinishedModal show={showAlreadyFinished} onGoToMeeting={() => navigate(successPageHref.current)} />
      <MuteWarningModal show={showMuteModal} onTimeout={handleMuteModalTimeout}></MuteWarningModal>
      {timeUntilExpiry && recordingStatus !== RecordingStatus.RECORDING && (
        <ExpireWarningModal
          show={showTokenExpiryWarning}
          sessionExpirationSeconds={timeUntilExpiry}
          onStartAnyWay={async () => {
            setShowTokenExpiryWarning(false)
            await _startRecording(true)
          }}
        />
      )}

      {sessionFinishInProgress ? (
        <FinishMeetingLoader />
      ) : preparationStopOnly && !startedFromMobileApp ? (
        <div className='skeleton pt-2' style={{ width: '100%', height: '60px' }}>
          <div className='d-flex flex-column align-items-center'>
            <div>It looks like this meeting is currently running in another tab or browser.</div>
            <div className='small-medium'>
              To end it, click <strong>"Remove AI Assistant"</strong>
            </div>
          </div>
        </div>
      ) : (
        <Container fluid className='d-flex flex-column align-items-center px-0'>
          <Row className='p-3 border zocks-border-0.5 w-100 align-items-center justify-content-between gap-3 position-relative'>
            <Col xs='auto' className={classNames('px-0', { 'blur-4': startedFromMobileApp })}>
              <Stack gap={4}>
                <Stack direction='horizontal' gap={2} className={classNames({ 'zocks-text-danger': isRecording })}>
                  {isRecording ? <i className='bi bi-broadcast' /> : null}
                  <div className='lead-small'>{!isRecording ? 'All set? Start the meeting!' : 'Live Session'}</div>
                </Stack>
                {!isRecording ? (
                  <Col>
                    <Stack direction='horizontal' gap={2}>
                      <ScopedBlock
                        scope={{
                          scope: 'room_session:start',
                          superScope: ['room_session:admin', 'room:admin'],
                        }}
                        missingScopeAction={MissingScopeAction.DISABLE}
                      >
                        <ScopedBlock authActions={[atRoomSessionLimitActions]} missingScopeAction={MissingScopeAction.DISABLE}>
                          <Button
                            disabled={!currentSession || !isSameLocalDate(new Date(currentSession.startedAt), new Date()) || startedFromMobileApp}
                            loading={startButtonLoading || mediaDevicesLoading}
                            variant='danger'
                            onClick={toggleRecording}
                          >
                            <i className={classNames({ 'bi bi-record-circle': !startButtonLoading })} /> Start
                          </Button>
                        </ScopedBlock>
                      </ScopedBlock>
                      <MediaDeviceSelector disabled={startedFromMobileApp} />
                    </Stack>
                  </Col>
                ) : (
                  <Stack direction='horizontal' gap={3}>
                    <Col className='recording-timer'>
                      <h2>
                        <Timer isRecording={isRecording} />
                      </h2>
                    </Col>
                    <Col xs='auto'>
                      <Stack direction='horizontal' gap={2}>
                        <Button loading={startButtonLoading} disabled={startButtonLoading} variant='darkGhost' onClick={togglePauseRecording}>
                          {recordingStatus === RecordingStatus.PAUSED ? (
                            <>
                              <i className='bi bi-pause-fill' /> Resume
                            </>
                          ) : (
                            <>
                              <i className='bi bi-record-circle' /> Pause
                            </>
                          )}
                        </Button>
                        <Button disabled={startButtonLoading || mediaDevicesLoading} loading={startButtonLoading} variant='danger' onClick={toggleRecording}>
                          <i className='bi bi-square-fill' /> Stop
                        </Button>
                      </Stack>
                    </Col>
                  </Stack>
                )}
              </Stack>
            </Col>
            {!startedFromMobileApp ? (
              <Col className='px-0'>
                <div className='mic-volume-level zocks-bg-color-primary-light'>
                  <MicVolumeLevel isRecording={isRecording} />
                </div>
              </Col>
            ) : (
              <>
                <Col className='px-0 d-flex align-items-center justify-content-center'>
                  <div className='mic-volume-level w-100 zocks-bg-color-primary-light blur-4'>
                    <div className='mic-volume-level-card'></div>
                  </div>
                </Col>
                <div className='position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center text-center'>
                  <i className='bi bi-phone-vibrate font-size-24' />
                  <div>
                    <span className='small-medium'>Control the meeting on mobile</span>
                  </div>
                  <div>To pause or stop the meeting, switch to mobile. Everything else works here.</div>
                </div>
              </>
            )}
          </Row>
        </Container>
      )}
    </>
  )
}
RecordingView.whyDidYouRender = true
